// src/index.js
import * as Sentry from "@sentry/react";
import { browserTracingIntegration } from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { ThemeProvider as MuiThemeProvider, CssBaseline } from "@mui/material";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import { createAppTheme } from "./theme";
import { AuthProvider } from "./contexts/AuthContext";
import useThemeStore, { selectThemeMode } from "./store/useThemeStore";
import useUserProfileStore from "./store/useUserProfileStore";
import {
  ProfiledSubtree,
  useRenderTrace,
  isRenderProfilingEnabled,
} from "./dev/renderProfiler";
import LimitedSessionNotice from "./components/shared/LimitedSessionNotice";

// -----------------------------
// App metadata
// -----------------------------
const APP_ENV = process.env.REACT_APP_ENV || "development";
const APP_VERSION = process.env.REACT_APP_VERSION;

// -----------------------------
// Initialize Sentry before React starts
// -----------------------------
Sentry.init({
  dsn: "https://7b8304e236fe0ac073397ef8deb5c12f@o4509804097699840.ingest.us.sentry.io/4509804098945024",
  integrations: [browserTracingIntegration()],
  tracesSampleRate: 1.0,
  release: `INSSA ${APP_ENV} ${APP_VERSION}`,
  environment: process.env.NODE_ENV,
});

window.__FIREBASE_DEBUG__ = true;

// -----------------------------
// Service Worker auto-update wiring (graceful reload)
// -----------------------------
// Detection is unchanged: the SW's own update lifecycle is the build-diff
// signal. What changes is the reload UX — instead of reloading the instant a
// new worker takes control (which can interrupt someone mid-task), we buffer
// the reload behind tab visibility so it happens between tasks, never during.
function setupServiceWorkerAutoUpdate() {
  if (!("serviceWorker" in navigator)) return;

  let reloading = false;
  let pendingReload = false;

  const reloadNow = () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  };

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    const controllerUrl = navigator.serviceWorker.controller?.scriptURL || "";
    // Ignore controller changes caused by non-app workers (e.g. FCM worker).
    if (!controllerUrl.includes("/service-worker.js")) return;
    // A new app build is in control. Reload at a non-disruptive moment.
    pendingReload = true;
    if (document.visibilityState === "hidden") reloadNow();
  });

  document.addEventListener("visibilitychange", () => {
    if (pendingReload) {
      reloadNow();
    } else if (document.visibilityState === "visible") {
      // Coming back to the tab — check for a fresh deploy right away.
      navigator.serviceWorker.getRegistration().then((r) => r && r.update());
    }
  });

  serviceWorkerRegistration.register({
    onUpdate: (registration) => {
      // New SW installed and waiting -> activate immediately
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    },
  });
}

if (process.env.NODE_ENV === "production") {
  setupServiceWorkerAutoUpdate();
} else {
  serviceWorkerRegistration.unregister(); // Disable in development
}

// -----------------------------
// Render
// -----------------------------
function AppRoot() {
  const themeMode = useThemeStore(selectThemeMode);
  const [systemPrefersDark, setSystemPrefersDark] = React.useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return true;
    }

    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const resolvedThemeMode = themeMode === "system" ? (systemPrefersDark ? "dark" : "light") : themeMode;
  const theme = React.useMemo(() => createAppTheme(resolvedThemeMode), [resolvedThemeMode]);
  useRenderTrace("AppRoot", { themeMode, resolvedThemeMode });

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const updateSystemTheme = (event) => {
      setSystemPrefersDark(event.matches);
    };

    setSystemPrefersDark(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateSystemTheme);
    } else {
      mediaQuery.addListener(updateSystemTheme);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", updateSystemTheme);
      } else {
        mediaQuery.removeListener(updateSystemTheme);
      }
    };
  }, []);

  React.useEffect(() => {
    document.documentElement.dataset.theme = resolvedThemeMode;
  }, [resolvedThemeMode]);

  React.useEffect(() => {
    if (!isRenderProfilingEnabled()) {
      return undefined;
    }

    const unsubscribeTheme = useThemeStore.subscribe((state, previousState) => {
      if (state.themeMode !== previousState.themeMode) {
        console.log("[render-profile] theme store update", {
          previousThemeMode: previousState.themeMode,
          nextThemeMode: state.themeMode,
        });
      }
    });

    const unsubscribeUserProfile = useUserProfileStore.subscribe((state, previousState) => {
      if (state.userProfile !== previousState.userProfile) {
        console.log("[render-profile] user profile store update", {
          previousUserId: previousState.userProfile?.uid ?? null,
          nextUserId: state.userProfile?.uid ?? null,
        });
      }
    });

    return () => {
      unsubscribeTheme();
      unsubscribeUserProfile();
    };
  }, []);

  return (
    <MuiThemeProvider theme={theme}>
      <StyledThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <ProfiledSubtree id="AppShell">
            <Sentry.ErrorBoundary fallback={<p>Something went wrong!</p>}>
              <App />
            </Sentry.ErrorBoundary>
          </ProfiledSubtree>
        </AuthProvider>
        <LimitedSessionNotice />
      </StyledThemeProvider>
    </MuiThemeProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <AppRoot />
  </React.StrictMode>
);

reportWebVitals();
